import { useDebouncedValue } from '@/hooks'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import {
    centroCusto,
    centroCusto_v2,
    empresas,
    funcionario,
    local,
} from 'service/api'
import type { PaginatedResponse } from 'service/api/types'
import { useStore } from 'store'
import { mapRenderCentros } from 'templates/Cadastro/CentroCusto/mapArvore'
import {
    type Escopo,
    type FacetaId,
    queryParamsDoEscopo,
    type Registro,
    um,
    varios,
} from './escopo'

/** situação que o servidor calcula para cada funcionário (#1870) */
export type SituacaoFuncionario = 'ativo' | 'emDesligamento' | 'demitido'

export type Opcao = {
    id: number
    label: string
    descricao?: string
    indent?: number
    situacao?: SituacaoFuncionario
}

export type FacetaDados = {
    opcoes: Opcao[]
    carregando: boolean
    vazioTexto: string
    /** só a faceta de funcionário pagina: há mais páginas no servidor */
    temMais?: boolean
    /** carrega a próxima página e devolve o que veio nela */
    carregarMais?: () => Promise<Opcao[]>
    carregandoMais?: boolean
}

export type FacetasDados = {
    empresas: FacetaDados
    centrosCusto: FacetaDados
    locais: FacetaDados
    funcionarios: FacetaDados
    /** funcionários que sobram depois do funil (empresa ∩ CC ∩ locais) */
    totalNoEscopo: number
    /** funcionários ativos da empresa, antes do funil */
    totalDaEmpresa: number
    /** funcionário escolhido; buscado por id quando não está nas páginas carregadas */
    funcionarioSelecionado?: Opcao
}

type FuncionarioDaFaceta = {
    id: number
    nome: string
    dtRescisao?: string | Date | null
    situacao?: SituacaoFuncionario
}

/** tamanho de página da lista de funcionário, igual ao do SelectFuncionario */
const FUNCIONARIOS_POR_PAGINA = 25

/**
 * Dados das quatro facetas. As buscas dependentes de empresa só rodam com
 * empresa escolhida, e só com o controle aberto.
 *
 * A faceta de funcionário é a única paginada: o servidor aplica o funil
 * (empresa ∩ centro de custo ∩ local), o recorte por situação e a busca, e a
 * contagem vem do total da resposta (#1870). Antes disso o front baixava a
 * lista inteira só para contar.
 */
/**
 * Quem saiu dentro da janela de `diasRescisao` ainda conta como ativo — o ponto
 * dele está em fechamento —, então o rótulo não o chama de demitido (#1870).
 */
function descricaoDaSituacao(funcionario: FuncionarioDaFaceta) {
    if (!funcionario.dtRescisao) return undefined

    const data = dayjs(funcionario.dtRescisao).format('DD/MM/YYYY')

    return funcionario.situacao === 'emDesligamento'
        ? `Rescisão em ${data}`
        : `Demitido(a) ${data}`
}

function opcaoDeFuncionario(funcionario: FuncionarioDaFaceta): Opcao {
    return {
        id: funcionario.id,
        label: funcionario.nome,
        situacao: funcionario.situacao,
        descricao: descricaoDaSituacao(funcionario),
    }
}

export function useFacetaOptions(
    escopo: Escopo,
    aberto: boolean,
    registro: Registro[] = [],
    busca = '',
): FacetasDados {
    const competencia = useStore(state => state.competencia)
    const funcionarioId = useStore(state => state.employeeId)
    const buscaDebounced = useDebouncedValue(busca)

    const empresaIds = varios(escopo.empresas)
    const dependeDeEmpresa = aberto && empresaIds.length > 0
    const isListaTudo = (id: FacetaId) =>
        registro.some(parte => parte.id === id && parte.listAll)

    const { data: empresasData = [], isFetching: carregandoEmpresas } =
        empresas.useFetch()

    const {
        data: centrosDaEmpresa = [],
        isFetching: carregandoCentrosEmpresa,
    } = centroCusto.useFetch({
        queryParams: { empresaIds },
        enabled: dependeDeEmpresa && !isListaTudo('centrosCusto'),
    })

    const {
        data: centrosDoCliente = [],
        isFetching: carregandoCentrosCliente,
    } = centroCusto_v2.useFetch({
        enabled: aberto && isListaTudo('centrosCusto'),
    })

    const { data: locaisDaEmpresa = [], isFetching: carregandoLocaisEmpresa } =
        local.useFetch({
            queryParams: { empresaIds },
            enabled: dependeDeEmpresa && !isListaTudo('locais'),
        })

    const { data: locaisDoCliente = [], isFetching: carregandoLocaisCliente } =
        local.all.useFetch({
            enabled: aberto && isListaTudo('locais'),
        })

    const incluirDemitidos = !!escopo.incluirDemitidos

    const {
        data: paginas,
        isFetching: carregandoFuncionarios,
        isFetchingNextPage: carregandoMaisFuncionarios,
        hasNextPage,
        fetchNextPage,
    } = funcionario.useInfiniteScroll<FuncionarioDaFaceta>({
        queryParams: {
            ...queryParamsDoEscopo(escopo),
            incluirDemitidos,
            competencia,
            ...(buscaDebounced && { search: buscaDebounced }),
        } as any,
        perPage: FUNCIONARIOS_POR_PAGINA,
        enabled: dependeDeEmpresa,
    })

    // total da empresa = antes do funil de centro de custo e local; uma página
    // de um item basta, o que interessa é o `meta.total`
    const { data: totalizador } = funcionario.useFetch<
        PaginatedResponse<FuncionarioDaFaceta>
    >({
        queryParams: {
            empresaIds,
            incluirDemitidos,
            competencia,
            page: 1,
            perPage: 1,
        } as any,
        enabled: dependeDeEmpresa,
    })

    const centrosData = isListaTudo('centrosCusto')
        ? centrosDoCliente
        : centrosDaEmpresa
    const carregandoCentros = isListaTudo('centrosCusto')
        ? carregandoCentrosCliente
        : carregandoCentrosEmpresa

    const locaisData = isListaTudo('locais') ? locaisDoCliente : locaisDaEmpresa
    const carregandoLocais = isListaTudo('locais')
        ? carregandoLocaisCliente
        : carregandoLocaisEmpresa

    const funcionariosCarregados = useMemo(
        () => paginas?.pages.flatMap(pagina => pagina.data) ?? [],
        [paginas],
    )

    const funcionarioEscolhidoId =
        funcionarioId > 0 ? funcionarioId : (um(escopo.funcionarios) ?? 0)
    const { data: funcionarioForaDaLista } =
        funcionario.useFetchById<FuncionarioDaFaceta>(funcionarioEscolhidoId, {
            enabled:
                !!funcionarioEscolhidoId &&
                !funcionariosCarregados.some(
                    item => item.id === funcionarioEscolhidoId,
                ),
        })

    return {
        empresas: {
            opcoes: empresasData.map(empresa => ({
                id: empresa.id,
                label: empresa.nomeFantasia,
            })),
            carregando: carregandoEmpresas,
            vazioTexto: 'Nenhuma empresa cadastrada',
        },
        centrosCusto: {
            opcoes: mapRenderCentros(centrosData).map(node => ({
                id: node.id,
                label: node.nomeCentroCusto,
                descricao: node.codigo,
                indent: node.space,
            })),
            carregando: carregandoCentros,
            vazioTexto: empresaIds.length
                ? 'Nenhum centro de custo cadastrado'
                : 'Escolha uma empresa primeiro',
        },
        locais: {
            opcoes: locaisData.map(item => ({
                id: item.id,
                label: item.nomeLocal,
            })),
            carregando: carregandoLocais,
            vazioTexto: empresaIds.length
                ? 'Nenhum local cadastrado'
                : 'Escolha uma empresa primeiro',
        },
        funcionarios: {
            opcoes: funcionariosCarregados.map(opcaoDeFuncionario),
            carregando: carregandoFuncionarios && !carregandoMaisFuncionarios,
            vazioTexto: empresaIds.length
                ? 'Nenhum funcionário encontrado a partir dos filtros'
                : 'Escolha uma empresa primeiro',
            temMais: !!hasNextPage,
            carregarMais: async () => {
                const resultado = await fetchNextPage()
                const novos = resultado.data?.pages.at(-1)?.data ?? []
                return novos.map(opcaoDeFuncionario)
            },
            carregandoMais: carregandoMaisFuncionarios,
        },
        totalNoEscopo: paginas?.pages[0]?.meta?.total ?? 0,
        totalDaEmpresa: totalizador?.meta?.total ?? 0,
        funcionarioSelecionado: [
            ...funcionariosCarregados,
            ...(funcionarioForaDaLista ? [funcionarioForaDaLista] : []),
        ]
            .filter(item => item.id === funcionarioEscolhidoId)
            .map(opcaoDeFuncionario)[0],
    }
}
